import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StickyFooter from '../components/StickyFooter';
import { uploadData, getUrl } from 'aws-amplify/storage';

type Reference = {
  id?: string;
  name: string;
  logoPath?: string;
  website?: string;
  contactPerson?: string;
  testimonial?: string;
  projectName?: string;
  projectDescription?: string;
  projectImage1?: string;
  projectImage2?: string;
  projectImage3?: string;
  projectImage4?: string;
  courseId?: string;
  isActive?: boolean;
};

interface AdminReferenceEditorProps {
  selectedReferenceId?: string;
  onBack: () => void;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

const AdminReferenceEditor: React.FC<AdminReferenceEditorProps> = ({ selectedReferenceId: propSelectedReferenceId, onBack }) => {
  const [selectedReferenceId, setSelectedReferenceId] = useState<string>(propSelectedReferenceId || '');
  const [form, setForm] = useState<Reference>({ name: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [availableReferences, setAvailableReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);

  const isEdit = Boolean(selectedReferenceId);

  // Fetch available references for selector
  const fetchAvailableReferences = async () => {
    try {
      const { data } = await client.models.Reference.list();
      setAvailableReferences(data as Reference[]);
    } catch (error) {
      console.error('Error fetching references:', error);
    }
  };

  // Handle reference selection from header dropdown
  const handleReferenceSelection = (referenceId: string) => {
    setSelectedReferenceId(referenceId);
    if (referenceId === '') {
      // Reset form for new reference
      setForm({ name: '', isActive: true });
      setLogoPreview('');
      setMessage(null);
    }
  };

  // Reset form function
  const handleReset = () => {
    if (selectedReferenceId) {
      // If editing, reload the reference data
      const selectedRef = availableReferences.find(ref => ref.id === selectedReferenceId);
      if (selectedRef) {
        setForm(selectedRef);
      }
    } else {
      // If creating new, reset to empty form
      setForm({ name: '', isActive: true });
    }
    setLogoPreview('');
    setMessage(null);
  };

  useEffect(() => {
    fetchAvailableReferences();
  }, []);

  useEffect(() => {
    if (!selectedReferenceId) return;
    (async () => {
      const { data } = await client.models.Reference.get({ id: selectedReferenceId });
      if (data) {
        setForm(data as any);
        if ((data as any).logoPath) {
          const path = (data as any).logoPath as string;
          try {
            const publicPath = path.startsWith('public/') ? path : `public/${path}`;
            const { url } = await getUrl({ path: publicPath });
            setLogoPreview(url.toString());
          } catch {
            setLogoPreview(path);
          }
        }
      }
    })();
  }, [selectedReferenceId]);

  // Inject selector into header
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="referenceSelect"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
        >
          <option value="" ${selectedReferenceId === '' ? 'selected' : ''}>Create New Reference</option>
          ${availableReferences.map(reference => 
            `<option value="${reference.id}" ${selectedReferenceId === reference.id ? 'selected' : ''}>${reference.name}</option>`
          ).join('')}
        </select>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          handleReferenceSelection((e.target as HTMLSelectElement).value);
        });
      }
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [selectedReferenceId, availableReferences]);

  const handleChange = (field: keyof Reference, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (field: keyof Reference, file: File) => {
    const safe = (form.name || 'reference').toLowerCase().replace(/[^a-z0-9\-]+/g, '-');
    const key = `references/${safe}/${Date.now()}_${file.name}`;
    await uploadData({ key, data: file, options: { contentType: file.type } }).result;
    handleChange(field, key);
    if (field === 'logoPath') {
      const { url } = await getUrl({ path: `public/${key}` });
      setLogoPreview(url.toString());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }
    
    setSaving(true);
    setMessage(null);
    
    try {
      if (isEdit && selectedReferenceId) {
        await client.models.Reference.update({ id: selectedReferenceId, ...(form as any) });
        setMessage({ type: 'success', text: 'Reference updated successfully!' });
      } else {
        const result = await client.models.Reference.create(form as any);
        setMessage({ type: 'success', text: 'Reference created successfully!' });
        // Refresh the available references list
        await fetchAvailableReferences();
        // Switch to editing the newly created reference
        if (result.data?.id) {
          setSelectedReferenceId(result.data.id);
        }
      }
    } catch (error) {
      console.error('Error saving reference:', error);
      setMessage({ type: 'error', text: 'Failed to save reference. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-form">
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Loading reference...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-form admin-editor-content">
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label>Company Name *</label>
            <input value={form.name} onChange={(e) => handleChange('name', e.target.value)} required />
          </div>
          <div className="form-field">
            <label>Website URL</label>
            <input value={form.website || ''} onChange={(e) => handleChange('website', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Contact Person</label>
            <input value={form.contactPerson || ''} onChange={(e) => handleChange('contactPerson', e.target.value)} />
          </div>
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label>Testimonial</label>
            <textarea value={form.testimonial || ''} onChange={(e) => handleChange('testimonial', e.target.value)} rows={2} />
          </div>
          <div className="form-field">
            <label>Project Name</label>
            <input value={form.projectName || ''} onChange={(e) => handleChange('projectName', e.target.value)} />
          </div>
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label>Project Description</label>
            <textarea value={form.projectDescription || ''} onChange={(e) => handleChange('projectDescription', e.target.value)} rows={4} />
          </div>
          <div className="form-field">
            <label>Course ID</label>
            <input value={form.courseId || ''} onChange={(e) => handleChange('courseId', e.target.value)} />
          </div>

          <div className="form-field">
            <label>Company Logo</label>
            <input type="file" accept="image/*" onChange={(e) => { const f=e.target.files?.[0]; if (f) handleImageUpload('logoPath', f); }} />
            {logoPreview && (
              <img src={logoPreview} alt="Logo preview" style={{ maxHeight: 60, marginTop: 8 }} />
            )}
          </div>

          {(['projectImage1','projectImage2','projectImage3','projectImage4'] as (keyof Reference)[]).map((field) => (
            <div className="form-field" key={field}>
              <label>{field}</label>
              <input type="file" accept="image/*" onChange={(e)=>{const f=e.target.files?.[0]; if(f) handleImageUpload(field, f);}} />
              {form[field] && <small>{String(form[field])}</small>}
            </div>
          ))}
        </div>

      </form>
      
      <StickyFooter
        onSave={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }}
        saving={saving}
        message={message}
        onMessageClose={() => setMessage(null)}
      />
    </div>
  );
};

export default AdminReferenceEditor;


